import * as React from 'react'
import { Info } from 'lucide-react'

import { cn } from '@/lib/utils'

interface TextareaProps extends React.ComponentProps<'textarea'> {
  hint?: string
  autoResize?: boolean
  autoResizeMaxHeight?: number
  floatingLabel?: boolean | string
  highlightBraceTokens?: boolean
  success?: boolean
  required?: boolean
}

function HighlightedText({ text }: { text: string }) {
  return text.split(/(\{\{[^}]+\}\})/g).map((segment, index) => (
    <span
      key={index}
      className={/^\{\{[^}]+\}\}$/.test(segment) ? 'text-creation-info-solid-surface-default' : undefined}
    >
      {segment}
    </span>
  ))
}

function Textarea({
  className,
  hint,
  value,
  maxLength,
  placeholder,
  disabled,
  autoResize,
  autoResizeMaxHeight,
  floatingLabel = true,
  highlightBraceTokens = false,
  success,
  required,
  onScroll,
  ...props
}: TextareaProps) {
  const internalRef = React.useRef<HTMLTextAreaElement>(null)

  React.useLayoutEffect(() => {
    if (!autoResize || !internalRef.current) return
    const el = internalRef.current
    el.style.height = 'auto'
    const newHeight = autoResizeMaxHeight != null ? Math.min(el.scrollHeight, autoResizeMaxHeight) : el.scrollHeight
    el.style.height = `${newHeight}px`
    el.style.overflowY = autoResizeMaxHeight != null && el.scrollHeight > autoResizeMaxHeight ? 'auto' : 'hidden'
  }, [value, autoResize, autoResizeMaxHeight])

  const textareaEl = (
    <div className={highlightBraceTokens ? 'relative' : undefined}>
      {highlightBraceTokens ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-px overflow-hidden whitespace-pre-wrap break-words px-3 py-2 text-comp-input-ip1 leading-comp-input-ip1 text-creation-placeholder-text-filled"
        >
          <HighlightedText text={typeof value === 'string' ? value : ''} />
        </div>
      ) : null}
      <textarea
        ref={internalRef}
        data-slot="textarea"
        data-success={success ? 'true' : undefined}
        value={value}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'w-full min-h-39 rounded-300 border border-creation-neutral-container-on-surface-default bg-creation-secondary-container-surface-default px-3 py-2 text-comp-input-ip1 leading-comp-input-ip1 text-creation-placeholder-text-filled transition-colors outline-none resize-none',
          'placeholder:text-creation-placeholder-text-default',
          'hover:border-creation-primary-container-on-surface-hover',
          'active:border-creation-primary-container-on-surface-pressed',
          'focus-visible:border-creation-primary-container-on-surface-focused focus-visible:shadow-[0_0_4px_var(--color-primary-a30)] focus-visible:ring-0',
          'disabled:cursor-not-allowed disabled:border-creation-neutral-container-on-surface-disable disabled:bg-creation-secondary-container-surface-disable disabled:text-creation-placeholder-text-disable disabled:opacity-100',
          'aria-invalid:border-creation-error-container-on-surface-default aria-invalid:ring-0',
          'data-[success=true]:border-creation-success-container-on-surface-default',
          highlightBraceTokens &&
            'relative bg-transparent text-transparent caret-creation-placeholder-text-filled selection:bg-[Highlight] selection:text-[HighlightText]',
          className,
        )}
        onScroll={(event) => {
          const overlay = event.currentTarget.previousElementSibling
          if (overlay instanceof HTMLDivElement) {
            overlay.scrollTop = event.currentTarget.scrollTop
            overlay.scrollLeft = event.currentTarget.scrollLeft
          }
          onScroll?.(event)
        }}
        {...props}
      />
    </div>
  )

  const floatingLabelText = typeof floatingLabel === 'string' ? floatingLabel : placeholder
  const hasPersistentFloatingLabel = typeof floatingLabel === 'string' && floatingLabel.trim().length > 0
  const showFloatingLabel = !!floatingLabel && !!floatingLabelText && (!!value || hasPersistentFloatingLabel)

  const coreEl =
    floatingLabel && (placeholder || hasPersistentFloatingLabel) ? (
      <div className={cn('relative flex flex-col rounded-300 overflow-visible', floatingLabel && 'mt-2')}>
        {showFloatingLabel && (
          <div
            className={cn(
              'absolute left-3 top-0 flex gap-0.5 -translate-y-1/2 px-1 text-ui-label-l1 leading-ui-label-l1 font-semibold z-10',
              'after:absolute after:inset-0 after:-z-10 after:top-1/2',
              disabled
                ? 'after:bg-creation-secondary-container-surface-disable'
                : 'after:bg-creation-secondary-container-surface-default',
            )}
          >
            <span className={disabled ? 'text-main-text-lv03' : 'text-main-text-lv01'}>{floatingLabelText}</span>
            {required && !disabled && <span className="text-main-text-error">*</span>}
          </div>
        )}
        {textareaEl}
      </div>
    ) : (
      textareaEl
    )

  if (!hint) return coreEl

  return (
    <div className="flex flex-col">
      {coreEl}
      <div className="flex items-start justify-between gap-1">
        <div className="flex items-center gap-1">
          <Info size={12} className="text-main-icon-lv02" />
          <span className="text-ui-caption-c1 leading-ui-caption-c1 text-main-icon-lv02">{hint}</span>
        </div>
        {maxLength != null && (
          <span className="text-ui-caption-c1 leading-ui-caption-c1 text-main-icon-lv02">
            {typeof value === 'string' ? value.length : 0}/{maxLength}
          </span>
        )}
      </div>
    </div>
  )
}

export { Textarea, HighlightedText }
export type { TextareaProps }
